import { useState } from "react";
import type { Contact, DiscoveryContext, Mode, Service, Vertical } from "../domain/types";

const modeOptions: Array<{ value: Mode; title: string; body: string; note: string }> = [
  {
    value: "public",
    title: "Think it through",
    body: "A private-on-your-device self-study. Nothing is sent unless you choose to send it at the end.",
    note: "No account · local save · useful brief",
  },
  {
    value: "private",
    title: "Work with pitch.dog",
    body: "A client discovery with confidential sources, decision owners, timing, and a resumable private copy.",
    note: "Local + server draft · private handoff",
  },
];

const verticalOptions: Array<{ value: Vertical; title: string; body: string }> = [
  { value: "film", title: "Film", body: "Narrative, documentary, short, feature, or hybrid." },
  { value: "tv", title: "TV / series", body: "Scripted, unscripted, limited, returning, or anthology." },
  { value: "advertising", title: "Advertising", body: "Director treatment, agency response, production or campaign pitch." },
  { value: "business", title: "Business", body: "Fundraising, sales, partnership, credentials, or internal decision." },
  { value: "general", title: "Something else", body: "We will find the nearest useful logic without forcing a false category." },
];

const serviceOptions: Array<{ value: Service; title: string; body: string }> = [
  { value: "writing", title: "Writing", body: "Find the argument, structure, voice, evidence, and ask." },
  { value: "design", title: "Design", body: "Clarify visual meaning, materials, rights, formats, and freedom." },
  { value: "writing_design", title: "Writing + design", body: "Build one story system. Keep responsibilities and change rules visible." },
];

type Props = {
  onStart: (context: DiscoveryContext & { contact: Contact }) => Promise<void> | void;
};

export function Intro({ onStart }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [mode, setMode] = useState<Mode | null>(null);
  const [vertical, setVertical] = useState<Vertical | null>(null);
  const [service, setService] = useState<Service | null>(null);
  const [starting, setStarting] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready = Boolean(name.trim() && emailValid && mode && vertical && service);
  const missing = [
    !name.trim() ? "your name" : null,
    !emailValid ? "a valid email" : null,
    !mode ? "how you are using this" : null,
    !vertical ? "a deck type" : null,
    !service ? "writing, design, or both" : null,
  ].filter(Boolean);

  async function begin() {
    setAttempted(true);
    setEmailTouched(true);
    if (!ready || !mode || !vertical || !service) return;
    setStarting(true);
    try {
      await onStart({
        mode,
        vertical,
        service,
        contact: {
          name: name.trim(),
          email: email.trim(),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        },
      });
    } finally {
      setStarting(false);
    }
  }

  return (
    <main className="intro" id="main">
      <form onSubmit={(event) => { event.preventDefault(); void begin(); }} noValidate>
      <header className="intro-hero">
        <p className="mono-label">PITCH.DOG · DECK DISCOVERY</p>
        <h1>Start before the slides.</h1>
        <p className="intro-copy">
          Find the decision, story, proof, and visual job your deck needs. Rough answers welcome. Honest unknowns too.
        </p>
        <div className="trust-line" aria-label="How saving works">
          <span>Saved as you go</span>
          <span>Back always works</span>
          <span>No upload wall</span>
        </div>
      </header>

      <section className="intro-section" aria-labelledby="contact-heading">
        <div className="section-number">01</div>
        <div>
          <h2 id="contact-heading">First, who is doing the thinking?</h2>
          <p className="section-copy">We ask now so your draft stays named and your finished answers can come back to you. Nothing is sent until the final handoff.</p>
          <div className="intro-contact-grid">
            <label>
              <span>Name</span>
              <input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required />
            </label>
            <label>
              <span>Email</span>
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() => setEmailTouched(true)}
                type="email"
                autoComplete="email"
                required
                aria-label="Email"
                aria-invalid={emailTouched && !emailValid}
                aria-describedby="intro-email-note"
              />
              <small className="input-note" id="intro-email-note" data-error={emailTouched && !emailValid}>
                {emailTouched && !emailValid ? "Use a complete email, like name@example.com." : "Your finished answer copy comes here."}
              </small>
            </label>
            <label>
              <span>Phone <small>optional</small></span>
              <input value={phone} onChange={(event) => setPhone(event.target.value)} type="tel" autoComplete="tel" />
            </label>
          </div>
        </div>
      </section>

      <section className="intro-section" aria-labelledby="mode-heading">
        <div className="section-number">02</div>
        <div>
          <h2 id="mode-heading">How are you using this?</h2>
          <div className="option-grid option-grid-two">
            {modeOptions.map((item) => (
              <button className="option-object" type="button" aria-pressed={mode === item.value} data-selected={mode === item.value} key={item.value} onClick={() => setMode(item.value)}>
                <span className="option-index">{mode === item.value ? "SELECTED" : "MODE"}</span>
                <strong>{item.title}</strong>
                <span>{item.body}</span>
                <small>{item.note}</small>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="intro-section" aria-labelledby="vertical-heading">
        <div className="section-number">03</div>
        <div>
          <h2 id="vertical-heading">What kind of deck?</h2>
          <div className="option-index-list">
            {verticalOptions.map((item) => (
              <button className="index-option" type="button" aria-pressed={vertical === item.value} data-selected={vertical === item.value} key={item.value} onClick={() => setVertical(item.value)}>
                <strong>{item.title}</strong>
                <span>{item.body}</span>
                <span className="selection-glyph" aria-hidden="true">{vertical === item.value ? "●" : "○"}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="intro-section" aria-labelledby="service-heading">
        <div className="section-number">04</div>
        <div>
          <h2 id="service-heading">What are we discovering?</h2>
          <div className="option-grid option-grid-three">
            {serviceOptions.map((item) => (
              <button className="option-object compact" type="button" aria-pressed={service === item.value} data-selected={service === item.value} key={item.value} onClick={() => setService(item.value)}>
                <strong>{item.title}</strong>
                <span>{item.body}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <footer className="intro-start">
        <p aria-live="polite" data-error={attempted && !ready}>
          {ready ? "Your route is ready." : `Still needed: ${missing.join(", ")}.`}
        </p>
        <button
          type="submit"
          className="ink-button"
          disabled={starting}
        >
          {starting ? "Preparing your route…" : "Begin discovery"}
        </button>
      </footer>
      </form>
    </main>
  );
}
